<!-- PrescriptionTab.vue -->
<template>
  <div>
    <!-- ═══════════════════════════════════════════════════════
         TAB SWITCHER
    ════════════════════════════════════════════════════════════ -->
    <div class="tabs flex border-b mb-4 gap-4">
      <button
        class="px-3 py-1 -mb-px font-medium border-b-2 transition-colors"
        :class="activeSection === 'internal'
          ? 'border-custom-yellow text-custom-blue'
          : 'border-transparent text-gray-500 hover:text-gray-700'"
        @click="activeSection = 'internal'"
      >
        Internal Prescription
      </button>
      <button
        class="px-3 py-1 -mb-px font-medium border-b-2 transition-colors"
        :class="activeSection === 'external'
          ? 'border-custom-yellow text-custom-blue'
          : 'border-transparent text-gray-500 hover:text-gray-700'"
        @click="activeSection = 'external'"
      >
        External Prescription
        <span
          v-if="externalPrescriptionSaved"
          class="ml-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full"
        >Saved</span>
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════════════
         INTERNAL PRESCRIPTION
    ════════════════════════════════════════════════════════════ -->
    <div v-show="activeSection === 'internal'">
      <div
        v-if="state === 'create' || state === 'edit'"
        class="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto"
      >
        <form class="space-y-4">
          <div class="grid grid-cols-2 gap-3 px-2">
            <div class="flex flex-col">
              <label class="font-medium">Drug:</label>
              <div class="flex items-center space-x-2">
                <v-select
                  class="flowbite-select flex-1"
                  v-model="prescriptionForm.inventory_item_id"
                  :options="drugs"
                  :filterable="false"
                  placeholder="Search for drugs"
                  @search="onSearch"
                  @option:selected="onDrugChange"
                  label="trade_name"
                  :loading="isLoading"
                >
                  <template #no-options="{ searching, loading }">
                    <template v-if="searching">No results found for "{{ searching }}"</template>
                    <template v-else-if="loading">Loading results...</template>
                    <template v-else>Type to search for drugs</template>
                  </template>
                  <template #option="option">
                    <div class="select-option">
                      <span>{{ option.trade_name }}</span>
                      <span v-if="option.generic_name"> - ({{ option.generic_name }})</span>
                      <span> - Avl Qty: {{ option.total_quantity }}</span>
                    </div>
                  </template>
                  <template #selected-option="option">
                    <div class="selected-option">{{ option.trade_name }}</div>
                  </template>
                </v-select>
              </div>
            </div>
            <div class="flex flex-col space-y-2">
              <label class="font-medium">Drug Strength:</label>
              <span>{{ selectedStrength }} {{ selectedStrengthUnit }}</span>
            </div>
            <div class="flex flex-col">
              <label class="font-medium">
                Dose: <span v-if="selectedStrengthUnit">({{ selectedStrengthUnit }})</span>
              </label>
              <input :disabled="state === 'view'" type="text" placeholder='e.g. 1 tsp, 1 capsule' v-model="prescriptionForm.dose"
                class="w-full border-gray-300 rounded p-1 bg-gray-50" required />
            </div>
            <div class="flex flex-col">
              <label class="font-medium">Dosage Frequency:</label>
              <select :disabled="state === 'view'" v-model="prescriptionForm.inventory_dosage_frequency_id"
                class="w-full border-gray-300 rounded p-1 bg-gray-50" required>
                <option v-for="freq in dosageFrequencies" :key="freq.id" :value="freq">{{ freq.name }}</option>
              </select>
            </div>
            <div class="flex flex-col">
              <label class="font-medium">Duration (days):</label>
              <input type="number" :disabled="state === 'view'" v-model="prescriptionForm.days"
                placeholder="e.g 15" class="w-full border-gray-300 rounded p-1 bg-gray-50" required />
            </div>
            <div class="flex flex-col">
              <label class="font-medium">Administration Route:</label>
              <select :disabled="state === 'view'" v-model="prescriptionForm.administration_route_id"
                class="w-full border-gray-300 rounded p-1 bg-gray-50" required>
                <option v-for="route in adminstrationRoutes" :key="route.id" :value="route">{{ route.name }}</option>
              </select>
            </div>

            <!-- Quantity: explicitly entered by doctor/pharmacist — no auto-fill -->
            <div class="flex flex-col">
              <label class="font-medium">Dispensing Quantity:<span class="text-red-500">*</span></label>
              <input
                type="number"
                min="1"
                :disabled="state === 'view'"
                v-model.number="prescriptionForm.quantity"
                placeholder="Enter units to dispense"
                class="w-full border-gray-300 rounded p-1 bg-gray-50"
                required
              />
            </div>

            <div class="flex flex-col">
              <label class="font-medium">Notes:</label>
              <textarea :disabled="state === 'view'" v-model="prescriptionForm.notes" placeholder="Type..."
                class="w-full border-gray-300 rounded p-1 bg-gray-50" rows="3"></textarea>
            </div>
          </div>
          <button v-if="prescriptionForm.inventory_item_id && !isAvailabilityConfirmed"
            @click="confirmAvailability" :disabled="isConfirmingAvailability"
            class="px-2 py-1 bg-orange-400 text-white rounded hover:bg-orange-500">
            {{ isConfirmingAvailability ? 'Confirming...' : 'Confirm Availability' }}
          </button>
          <div v-if="isAvailabilityConfirmed" class="flex justify-end px-2">
            <button type="button" @click="addPrescriptions" :disabled="state === 'view'"
              :class="['py-1 px-4 rounded', state === 'view' ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-custom-yellow text-white hover:bg-custom-blue']">
              Add Prescription
            </button>
          </div>
        </form>

        <div v-if="prescriptions.length > 0" class="mt-2">
          <h2 class="text-lg font-semibold mb-4">Prescriptions</h2>
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-2 text-left text-md font-medium text-gray-500">Drug</th>
                <th class="px-6 py-2 text-left text-md font-medium text-gray-500">Strength</th>
                <th class="px-6 py-2 text-left text-md font-medium text-gray-500">Dosage</th>
                <th class="px-6 py-2 text-left text-md font-medium text-gray-500">Frequency</th>
                <th class="px-6 py-2 text-left text-md font-medium text-gray-500">Duration</th>
                <th class="px-6 py-2 text-left text-md font-medium text-gray-500">Administration Route</th>
                <th class="px-6 py-2 text-left text-md font-medium text-gray-500">Quantity</th>
                <th class="px-6 py-2 text-left text-md font-medium text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(prescription, index) in prescriptions" :key="index">
                <template v-if="prescription?.status?.code !== 'ST-019'">
                  <td class="px-6 py-1 whitespace-nowrap">{{ prescription.inventory_item_id?.trade_name || "-" }}</td>
                  <td class="px-6 py-1 whitespace-nowrap">{{ prescription.inventory_item_id?.strength || "-" }} {{ prescription.inventory_item_id?.drug_strength_unit?.name || "-" }}</td>
                  <td class="px-6 py-1 whitespace-nowrap">{{ prescription.dose || "-" }}</td>
                  <td class="px-6 py-1 whitespace-nowrap">{{ prescription.inventory_dosage_frequency_id?.name || "-" }}</td>
                  <td class="px-6 py-1 whitespace-nowrap">{{ prescription.days ? `${prescription.days} (days)` : "-" }}</td>
                  <td class="px-6 py-1 whitespace-nowrap">{{ prescription.administration_route_id?.name || "-" }}</td>
                  <td class="px-6 py-1 whitespace-nowrap">{{ prescription.quantity || "-" }}</td>
                  <td class="px-6 py-1 whitespace-nowrap">
                    <DeleteIcon @click.prevent="triggerPrescriptionDeleteModal(prescription.id)"
                      v-if="prescription.existing && prescription.status?.code !== 'ST-016'" />
                    <button v-if="!prescription.id" @click="deletePrescription(index)"
                      class="text-gray-50 bg-red-500 hover:text-gray-600 px-4 py-1 rounded-md">Delete</button>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="state === 'view'" class="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
        <div v-if="existingPrescriptions.length > 0">
          <div v-for="(prescription, index) in existingPrescriptions" :key="index">
            <div><p class="font-semibold">Drug:</p><p>{{ prescription.inventory_item?.trade_name || "-" }}</p></div>
            <div><p class="font-semibold">Dosage:</p><p>{{ prescription.dose || "-" }}</p></div>
            <div><p class="font-semibold">Frequency:</p><p>{{ prescription.inventory_dosage_frequency?.name || "-" }}</p></div>
            <div><p class="font-semibold">Duration:</p><p>{{ prescription.days ? `${prescription.days} days` : "-" }}</p></div>
            <div><p class="font-semibold">Route:</p><p>{{ prescription.administration_route?.name || "-" }}</p></div>
            <div><p class="font-semibold">Notes:</p><p>{{ prescription.notes || "-" }}</p></div>
          </div>
        </div>
        <div v-else>No prescriptions available.</div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════
         EXTERNAL PRESCRIPTION
    ════════════════════════════════════════════════════════════ -->
    <div v-show="activeSection === 'external'" class="max-h-[60vh] overflow-y-auto px-1">

      <!-- Saved summary panel -->
      <div v-if="showExternalSummaryCard"
        class="mb-4 border border-green-200 rounded-lg bg-green-50 p-4">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="text-sm font-semibold text-green-800">Saved External Prescription</span>
          </div>
          <button @click="restoreFromSaved"
            class="px-3 py-1 bg-custom-yellow text-white rounded text-xs font-medium hover:bg-custom-blue">
            Edit Prescription
          </button>
        </div>
        <p v-if="lastSavedSnapshot.instructions" class="text-xs text-gray-600 italic mb-2">
          "{{ lastSavedSnapshot.instructions }}"
        </p>
        <table class="w-full text-xs">
          <thead>
            <tr class="border-b border-green-200">
              <th class="text-left py-1 pr-2 text-gray-600 font-semibold">Drug</th>
              <th class="text-left py-1 pr-2 text-gray-600 font-semibold">Dose</th>
              <th class="text-left py-1 pr-2 text-gray-600 font-semibold">Frequency</th>
              <th class="text-left py-1 pr-2 text-gray-600 font-semibold">Duration</th>
              <th class="text-left py-1 pr-2 text-gray-600 font-semibold">Qty</th>
              <th class="text-left py-1 text-gray-600 font-semibold">Route</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in lastSavedSnapshot.items" :key="idx" class="border-b border-green-100">
              <td class="py-1 pr-2 font-medium text-gray-800">{{ item.drug_name }}</td>
              <td class="py-1 pr-2 text-gray-600">{{ item.dose ? `${item.dose} ${item.dose_unit || ''}`.trim() : '-' }}</td>
              <td class="py-1 pr-2 text-gray-600">{{ item.dosage_frequency?.name || '-' }}</td>
              <td class="py-1 pr-2 text-gray-600">{{ item.days ? `${item.days}d` : '-' }}</td>
              <td class="py-1 pr-2 text-gray-600">{{ item.quantity || '-' }}</td>
              <td class="py-1 text-gray-600">{{ item.administration_route?.name || '-' }}</td>
            </tr>
          </tbody>
        </table>
        <div class="mt-3 flex justify-end">
          <button @click="printExternalPrescription"
            class="px-3 py-1 bg-custom-blue text-white rounded text-xs font-medium hover:opacity-90 flex items-center gap-1">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print / Download
          </button>
        </div>
      </div>

      <!-- Drug item form rows -->
      <template v-if="showExternalForm">
        <div class="mb-4">
          <label class="font-medium text-sm block mb-1">Prescription Instructions / Notes:</label>
          <textarea v-model="extForm.instructions"
            placeholder="e.g. Take medication after meals. Complete full course. Return if symptoms persist after 5 days."
            class="w-full border border-gray-300 rounded p-2 bg-gray-50 text-sm" rows="3"></textarea>
        </div>
        <div class="space-y-3">
          <div v-for="(item, index) in extForm.items" :key="index"
            class="border border-gray-200 rounded-lg p-3 bg-gray-50 relative">
            <button @click="removeExtItem(index)"
              class="absolute top-2 right-2 text-white bg-red-500 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold leading-none"
              title="Remove drug">x</button>
            <div class="grid grid-cols-2 gap-3 pr-8">
              <div class="flex flex-col">
                <label class="text-sm font-medium">Drug Name: <span class="text-red-500">*</span></label>
                <input type="text" v-model="item.drug_name" placeholder="e.g. Amoxicillin"
                  class="border border-gray-300 rounded p-1 bg-white text-sm mt-1" />
              </div>
              <div class="flex flex-col">
                <label class="text-sm font-medium">Dosage Form:</label>
                <input type="text" v-model="item.drug_dosage_form" placeholder="e.g. Tablet, Syrup, Capsule"
                  class="border border-gray-300 rounded p-1 bg-white text-sm mt-1" />
              </div>
              <div class="flex flex-col">
                <label class="text-sm font-medium">Dose:</label>
                <div class="flex gap-1 mt-1">
                  <input type="number" v-model="item.dose" placeholder="500"
                    class="border border-gray-300 rounded p-1 bg-white text-sm w-2/3" />
                  <input type="text" v-model="item.dose_unit" placeholder="mg / ml"
                    class="border border-gray-300 rounded p-1 bg-white text-sm w-1/3" />
                </div>
              </div>
              <div class="flex flex-col">
                <label class="text-sm font-medium">Frequency:</label>
                <select v-model="item.inventory_dosage_frequency_id"
                  class="border border-gray-300 rounded p-1 bg-white text-sm mt-1">
                  <option value="">-- Select --</option>
                  <option v-for="freq in dosageFrequencies" :key="freq.id" :value="freq.id">{{ freq.name }}</option>
                </select>
              </div>
              <div class="flex flex-col">
                <label class="text-sm font-medium">Duration (days):</label>
                <input type="number" v-model="item.days" placeholder="e.g. 7"
                  class="border border-gray-300 rounded p-1 bg-white text-sm mt-1" />
              </div>
              <div class="flex flex-col">
                <label class="text-sm font-medium">Quantity: <span class="text-red-500">*</span></label>
                <input type="number" min="1" v-model="item.quantity" placeholder="Enter units to dispense"
                  class="border border-gray-300 rounded p-1 bg-white text-sm mt-1" />
              </div>
              <div class="flex flex-col">
                <label class="text-sm font-medium">Administration Route:</label>
                <select v-model="item.administration_route_id"
                  class="border border-gray-300 rounded p-1 bg-white text-sm mt-1">
                  <option value="">-- Select --</option>
                  <option v-for="route in adminstrationRoutes" :key="route.id" :value="route.id">{{ route.name }}</option>
                </select>
              </div>
              <div class="flex flex-col">
                <label class="text-sm font-medium">Item Notes:</label>
                <input type="text" v-model="item.notes" placeholder="e.g. Take with food"
                  class="border border-gray-300 rounded p-1 bg-white text-sm mt-1" />
              </div>
            </div>
          </div>
        </div>
        <button @click="addExtItem"
          class="mt-3 px-4 py-1.5 border border-dashed border-custom-yellow text-custom-blue rounded hover:bg-yellow-50 text-sm font-medium">
          + Add Drug
        </button>
        <div class="flex justify-between items-center mt-4 pt-3 border-t">
          <span v-if="extSaveError" class="text-red-500 text-sm">{{ extSaveError }}</span>
          <div class="flex gap-2 ml-auto">
            <button v-if="isEditingExternal" @click="cancelEditExternal" :disabled="isSavingExt"
              class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm font-medium disabled:opacity-50">
              Cancel
            </button>
            <button @click="saveExternalPrescription" :disabled="isSavingExt"
              class="px-4 py-1.5 bg-custom-yellow text-white rounded hover:bg-custom-blue text-sm font-medium disabled:opacity-50">
              {{ isSavingExt ? 'Saving...' : (externalPrescriptionSaved ? 'Save Changes' : 'Save Prescription') }}
            </button>
            <button v-if="externalPrescriptionSaved" @click="printExternalPrescription"
              class="px-4 py-1.5 bg-custom-blue text-white rounded hover:opacity-90 text-sm font-medium flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print / Download
            </button>
          </div>
        </div>
      </template>

      <!-- Empty state -->
      <div v-if="showExternalEmptyState" class="text-center py-8 text-gray-400">
        <svg class="w-10 h-10 mx-auto mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <p class="text-sm">No external prescription yet.</p>
        <button @click="addExtItem"
          class="mt-2 px-4 py-1.5 bg-custom-yellow text-white rounded text-sm font-medium hover:bg-custom-blue">
          + Add Drug
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════
         PRINTABLE PRESCRIPTION DOCUMENT
    ════════════════════════════════════════════════════════════ -->
    <div id="ext-prescription-print" style="display:none;">
      <div class="print-doc">
        <div class="print-header">
          <div class="print-identity">
            <img v-if="facilityLogoUrl" :src="facilityLogoUrl" class="print-logo-center" alt="Facility Logo" />
            <div v-else class="print-logo-center-placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="width:56px;height:56px;color:#1a365d">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h1 class="print-facility-name-center">{{ facilityName }}</h1>
            <p v-if="facilityLocation" class="print-facility-location">{{ facilityLocation }}</p>
            <p v-if="facilityPhone" class="print-facility-phone">Tel: {{ facilityPhone }}</p>
          </div>
          <div class="print-title-band"><h2>PRESCRIPTION</h2></div>
          <div class="print-meta">
            <div class="print-meta-col">
              <p><strong>Patient Name:</strong> {{ patientName }}</p>
              <p><strong>Patient ID:</strong> {{ patientCode }}</p>
              <p><strong>Date of Birth:</strong> {{ patientDob }}</p>
            </div>
            <div class="print-meta-col">
              <p><strong>Gender:</strong> {{ patientGender }}</p>
              <p><strong>Visit Date:</strong> {{ visitDate }}</p>
              <p><strong>Prescriber:</strong> {{ prescriberName }}</p>
            </div>
          </div>
        </div>
        <div v-if="extForm.instructions" class="print-instructions">
          <strong>Instructions:</strong> {{ extForm.instructions }}
        </div>
        <table class="print-table">
          <thead>
            <tr>
              <th>#</th><th>Drug</th><th>Form</th><th>Dose</th>
              <th>Frequency</th><th>Duration</th><th>Route</th><th>Qty</th><th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in printItems" :key="idx">
              <td>{{ idx + 1 }}</td>
              <td><strong>{{ item.drug_name }}</strong></td>
              <td>{{ item.drug_dosage_form || '-' }}</td>
              <td>{{ item.dose ? `${item.dose} ${item.dose_unit || ''}`.trim() : '-' }}</td>
              <td>{{ getDosageFrequencyName(item.inventory_dosage_frequency_id) }}</td>
              <td>{{ item.days ? `${item.days} day(s)` : '-' }}</td>
              <td>{{ getRouteName(item.administration_route_id) }}</td>
              <td>{{ item.quantity || '-' }}</td>
              <td>{{ item.notes || '-' }}</td>
            </tr>
          </tbody>
        </table>
        <div class="print-signature">
          <div class="sig-block">
            <div class="sig-line"></div>
            <p>Prescriber's Signature &amp; Stamp</p>
            <p><strong>{{ prescriberName }}</strong></p>
          </div>
          <div class="sig-block">
            <div class="sig-line"></div>
            <p>Date of Issue</p>
            <p><strong>{{ visitDate }}</strong></p>
          </div>
        </div>
        <div class="print-footer">
          <p class="print-powered">Powered by JiJi Health &mdash; {{ facilityName }}</p>
        </div>
      </div>
    </div>

    <ConfirmationModal
      :show="showPrescriptionDeleteConfirm"
      :showLoader="showPrescriptionDeleteLoader"
      title="Delete Saved Prescription"
      message="Are you sure you want to delete saved prescription, this action cannot be undone?"
      @cancel="closeConfirmationModal"
      @confirm="deleteSavedConsultation"
    />

    <ConfirmationModal
      :show="showAllergyConfirm"
      title="Allergy Warning"
      :warningText="allergyMessage"
      message="Do you want to prescribe this anyway?"
      @cancel="cancelAllergySelection"
      @confirm="confirmAllergyOverride"
    />
  </div>
</template>

<script>
import vSelect from "vue-select";
import apiClient from "@/services/api";
import "vue-select/dist/vue-select.css";
import DeleteIcon from "@/components/buttons/DeleteIcon.vue";
import ConfirmationModal from "@/views/modals/shared/ConfirmationModal.vue";

export default {
  name: "PrescriptionTab",
  components: { ConfirmationModal, DeleteIcon, vSelect },

  props: {
    state: { type: String, required: true, validator: (v) => ["create", "edit", "view"].includes(v) },
    visitId:                      { type: String, required: true },
    existingPrescriptions:        { type: Array,  default: () => [] },
    existingExternalPrescription: { type: Object, default: null },
    visitDetails:                 { type: Object, default: null },
    patientId: { type: String, default: null }
  },

  data() {
    return {
      activeSection: "internal",
      prescriptions: [],
      drugs: [],
      adminstrationRoutes: [],
      dosageFrequencies: [],
      selectDosageForm: "",
      selectedStrength: "",
      selectedStrengthUnit: "",
      selectedDrugDosageForm: "",
      selectedRate: "",
      isLoading: false,
      isConfirmingAvailability: false,
      isAvailabilityConfirmed: false,
      showPrescriptionDeleteConfirm: false,
      showPrescriptionDeleteLoader: false,
      selectedExistingPrescription: "",
      searchTimeout: null,
      prescriptionForm: {
        inventory_item_id: null, dose: "", inventory_dosage_frequency_id: null,
        days: "", administration_route_id: null, unit_price: 200,
        bill_category: 4, quantity: "", notes: "", status: {},
      },
      externalPrescriptionSaved: false,
      isEditingExternal: false,
      isSavingExt: false,
      extSaveError: "",
      extForm: { instructions: "", items: [] },
      lastSavedSnapshot: null,
      showAllergyConfirm: false,
      allergyMessage: "",
      allergyAcknowledged: false,
    };
  },

  mounted() {
    this.fetchAdminstrationRoutes();
    this.fetchDosageFrequencies();
  },

  watch: {
    existingPrescriptions: {
      handler(newItems) {
        this.prescriptions = [];
        if (newItems?.length > 0) {
          newItems.forEach((drug) => {
            this.prescriptions.push({
              ...drug,
              inventory_item_id:             drug.inventory_item,
              administration_route_id:       drug.administration_route,
              inventory_dosage_frequency_id: drug.inventory_dosage_frequency,
              existing: true,
            });
          });
        }
      },
      immediate: true,
      deep: true,
    },
    existingExternalPrescription: {
      handler(val) {
        if (val) {
          this.externalPrescriptionSaved = true;
          this.loadExternalPrescriptionIntoForm(val);
          this.lastSavedSnapshot = this.buildSnapshot(val.instructions || "", val.items || []);
        }
      },
      immediate: true,
    },
    // Reset availability confirmation whenever key prescription fields change
    "prescriptionForm.inventory_item_id"()            { this.isAvailabilityConfirmed = false; },
    "prescriptionForm.dose"()                         { this.isAvailabilityConfirmed = false; },
    "prescriptionForm.days"()                         { this.isAvailabilityConfirmed = false; },
    "prescriptionForm.inventory_dosage_frequency_id"(){ this.isAvailabilityConfirmed = false; },
    // Quantity change also resets confirmation — user must re-confirm if they change qty
    "prescriptionForm.quantity"()                     { this.isAvailabilityConfirmed = false; },
  },

  computed: {
    // These three drive which of the External Prescription panels renders.
    // Deliberately NOT keyed on extForm.items.length alone — the
    // existingExternalPrescription watcher always pre-fills extForm (so
    // "Edit Prescription" has something to start from), which used to make
    // the saved-summary card's old `extForm.items.length === 0` guard false
    // immediately on every reload, permanently hiding it behind the edit form.
    showExternalSummaryCard() {
      return this.externalPrescriptionSaved && !this.isEditingExternal && !!this.lastSavedSnapshot;
    },
    showExternalForm() {
      return !this.showExternalSummaryCard && this.extForm.items.length > 0;
    },
    showExternalEmptyState() {
      return !this.showExternalSummaryCard && this.extForm.items.length === 0;
    },
    facilityName() {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      return this.visitDetails?.facility?.FacilityName || stored?.FacilityName || "";
    },
    facilityLocation() {
      const f = this.visitDetails?.facility;
      if (!f) return "";
      return [f.NearestTown, f.County].filter(Boolean).join(", ");
    },
    facilityPhone()   { return this.visitDetails?.facility?.phone || ""; },
    facilityLogoUrl() { return this.visitDetails?.facility?.logo_url || null; },
    patientName()     { return this.visitDetails?.patient?.FullName || ""; },
    patientCode()     { return this.visitDetails?.patient?.unique_code || ""; },
    patientDob()      { return this.visitDetails?.patient?.Dob || ""; },
    patientGender()   { return this.visitDetails?.patient?.Gender || ""; },
    visitDate() {
      const d = this.visitDetails?.visitDate;
      if (!d) return new Date().toLocaleDateString("en-GB");
      return new Date(d).toLocaleDateString("en-GB");
    },
    prescriberName() {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      return this.visitDetails?.prescriber || stored?.Username || "";
    },
    printItems() {
      if (this.extForm.items.length > 0) return this.extForm.items;
      if (this.existingExternalPrescription?.items?.length) {
        return this.existingExternalPrescription.items.map((item) => ({
          drug_name: item.drug_name, drug_dosage_form: item.drug_dosage_form,
          dose: item.dose, dose_unit: item.dose_unit,
          inventory_dosage_frequency_id: item.inventory_dosage_frequency_id,
          administration_route_id: item.administration_route_id,
          days: item.days, quantity: item.quantity, notes: item.notes,
        }));
      }
      return [];
    },
  },

  methods: {
    loadExternalPrescriptionIntoForm(val) {
      this.extForm.instructions = val.instructions || "";
      this.extForm.items = (val.items || []).map((item) => ({
        drug_name: item.drug_name || "", drug_dosage_form: item.drug_dosage_form || "",
        dose: item.dose || "", dose_unit: item.dose_unit || "",
        inventory_dosage_frequency_id: item.inventory_dosage_frequency_id || "",
        administration_route_id: item.administration_route_id || "",
        days: item.days || "", quantity: item.quantity || "", notes: item.notes || "",
      }));
    },
    restoreFromSaved() {
      const source = this.lastSavedSnapshot || this.existingExternalPrescription;
      if (source) this.loadExternalPrescriptionIntoForm(source);
      this.isEditingExternal = true;
    },
    cancelEditExternal() {
      const source = this.lastSavedSnapshot || this.existingExternalPrescription;
      if (source) this.loadExternalPrescriptionIntoForm(source);
      this.isEditingExternal = false;
    },
    addExtItem() {
      this.extForm.items.push({
        drug_name: "", drug_dosage_form: "", dose: "", dose_unit: "",
        inventory_dosage_frequency_id: "", administration_route_id: "",
        days: "", quantity: "", notes: "",
      });
    },
    removeExtItem(index) { this.extForm.items.splice(index, 1); },
    getDosageFrequencyName(id) { return this.dosageFrequencies.find((f) => f.id === id)?.name || "-"; },
    getRouteName(id) { return this.adminstrationRoutes.find((r) => r.id === id)?.name || "-"; },

    async saveExternalPrescription() {
      if (this.extForm.items.length === 0) { this.$toast.error("Add at least one drug before saving."); return; }
      if (this.extForm.items.some((i) => !i.drug_name?.trim())) { this.$toast.error("Please fill in the drug name for all items."); return; }
      this.isSavingExt = true;
      this.extSaveError = "";
      try {
        const payload = {
          visit_id: this.visitId, patient_id: this.visitDetails?.patient?.PatientId,
          instructions: this.extForm.instructions,
          items: this.extForm.items.map((item) => ({
            drug_name: item.drug_name, drug_dosage_form: item.drug_dosage_form || null,
            dose: item.dose || null, dose_unit: item.dose_unit || null,
            inventory_dosage_frequency_id: item.inventory_dosage_frequency_id || null,
            administration_route_id: item.administration_route_id || null,
            days: item.days || null, quantity: item.quantity || null, notes: item.notes || null,
          })),
        };
        const response = await apiClient.saveExternalPrescription(payload);
        if (response.success) {
          this.externalPrescriptionSaved = true;
          this.isEditingExternal = false;
          this.lastSavedSnapshot = this.buildSnapshot(this.extForm.instructions, this.extForm.items);
          this.$toast.success("External prescription saved successfully.");
        } else {
          this.extSaveError = "Failed to save. Please try again.";
          this.$toast.error("Failed to save external prescription.");
        }
      } catch {
        this.extSaveError = "Network error. Please check your connection.";
        this.$toast.error("Network error saving external prescription.");
      } finally { this.isSavingExt = false; }
    },

    buildSnapshot(instructions, items) {
      return {
        instructions: instructions || "",
        items: (items || []).map((i) => ({
          drug_name:             i.drug_name || "",
          drug_dosage_form:      i.drug_dosage_form || "",
          dose:                  i.dose || "",
          dose_unit:             i.dose_unit || "",
          quantity:              i.quantity || "",
          days:                  i.days || "",
          notes:                 i.notes || "",
          dosage_frequency_name: this.getDosageFrequencyName(
            i.inventory_dosage_frequency_id || i.dosage_frequency?.id
          ),
          route_name: this.getRouteName(
            i.administration_route_id || i.administration_route?.id
          ),
          inventory_dosage_frequency_id: i.inventory_dosage_frequency_id || i.dosage_frequency?.id || "",
          administration_route_id:       i.administration_route_id || i.administration_route?.id || "",
        })),
      };
    },

    esc(str) {
      if (str == null) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },

    printExternalPrescription() {
      const itemsToPrint = this.extForm.items.length > 0
        ? this.extForm.items.map((i) => ({
            drug_name:             i.drug_name || "",
            drug_dosage_form:      i.drug_dosage_form || "",
            dose:                  i.dose || "",
            dose_unit:             i.dose_unit || "",
            quantity:              i.quantity || "",
            days:                  i.days || "",
            notes:                 i.notes || "",
            dosage_frequency_name: this.getDosageFrequencyName(i.inventory_dosage_frequency_id),
            route_name:            this.getRouteName(i.administration_route_id),
          }))
        : (this.lastSavedSnapshot?.items || []);

      const instructionsToPrint = this.extForm.items.length > 0
        ? this.extForm.instructions
        : (this.lastSavedSnapshot?.instructions || "");

      if (itemsToPrint.length === 0) {
        this.$toast.error("Nothing to print yet. Please save a prescription first.");
        return;
      }

      const esc = this.esc.bind(this);

      const rowsHtml = itemsToPrint.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td><strong>${esc(item.drug_name)}</strong></td>
          <td>${esc(item.drug_dosage_form) || '-'}</td>
          <td>${item.dose ? `${esc(String(item.dose))} ${esc(item.dose_unit || '')}`.trim() : '-'}</td>
          <td>${esc(item.dosage_frequency_name) || '-'}</td>
          <td>${item.days ? `${esc(String(item.days))} day(s)` : '-'}</td>
          <td>${esc(item.route_name) || '-'}</td>
          <td>${esc(String(item.quantity || '-'))}</td>
          <td>${esc(item.notes) || '-'}</td>
        </tr>`).join("");

      const instructionsHtml = instructionsToPrint
        ? `<div style="margin:12px 0;padding:8px 14px;background:#fffbeb;border-left:4px solid #d4a017;font-size:10.5pt;color:#333;border-radius:2px;">
             <strong>Instructions:</strong> ${esc(instructionsToPrint)}
           </div>` : "";

      const logoHtml = this.facilityLogoUrl
        ? `<img src="${this.facilityLogoUrl}" style="max-height:80px;max-width:160px;object-fit:contain;margin-bottom:8px;" alt="Logo" />`
        : `<div style="margin-bottom:8px;">
             <svg viewBox="0 0 24 24" fill="none" stroke="#1a365d" stroke-width="1.5" style="width:56px;height:56px;">
               <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
             </svg>
           </div>`;

      const printHtml = `
        <div style="font-family:'Georgia','Times New Roman',serif;font-size:11pt;color:#111;padding:16mm 20mm;max-width:210mm;margin:0 auto;">
          <div style="border-bottom:3px double #1a365d;padding-bottom:12px;margin-bottom:14px;">
            <div style="display:flex;flex-direction:column;align-items:center;text-align:center;margin-bottom:12px;">
              ${logoHtml}
              <h1 style="font-size:20pt;font-weight:bold;color:#1a365d;margin:0 0 4px 0;line-height:1.2;">${esc(this.facilityName)}</h1>
              ${this.facilityLocation ? `<p style="font-size:10.5pt;color:#444;margin:0 0 2px 0;">${esc(this.facilityLocation)}</p>` : ""}
              ${this.facilityPhone ? `<p style="font-size:9.5pt;color:#666;margin:0;">Tel: ${esc(this.facilityPhone)}</p>` : ""}
            </div>
            <div style="background:#1a365d;color:#fff;text-align:center;padding:5px 0;border-radius:4px;margin:0 0 10px 0;">
              <h2 style="margin:0;font-size:13pt;letter-spacing:4px;font-weight:700;">PRESCRIPTION</h2>
            </div>
            <div style="display:flex;gap:30px;margin-top:10px;">
              <div style="flex:1;">
                <p style="margin:4px 0;font-size:10.5pt;"><strong>Patient Name:</strong> ${esc(this.patientName)}</p>
                <p style="margin:4px 0;font-size:10.5pt;"><strong>Patient ID:</strong> ${esc(this.patientCode)}</p>
                <p style="margin:4px 0;font-size:10.5pt;"><strong>Date of Birth:</strong> ${esc(this.patientDob)}</p>
              </div>
              <div style="flex:1;">
                <p style="margin:4px 0;font-size:10.5pt;"><strong>Gender:</strong> ${esc(this.patientGender)}</p>
                <p style="margin:4px 0;font-size:10.5pt;"><strong>Visit Date:</strong> ${esc(this.visitDate)}</p>
                <p style="margin:4px 0;font-size:10.5pt;"><strong>Prescriber:</strong> ${esc(this.prescriberName)}</p>
              </div>
            </div>
          </div>
          ${instructionsHtml}
          <table style="width:100%;border-collapse:collapse;margin:14px 0;font-size:10pt;">
            <thead>
              <tr>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">#</th>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">Drug</th>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">Form</th>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">Dose</th>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">Frequency</th>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">Duration</th>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">Route</th>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">Qty</th>
                <th style="background:#1a365d;color:#fff;padding:7px 8px;text-align:left;font-weight:600;font-size:9.5pt;">Notes</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <div style="display:flex;justify-content:space-between;margin-top:50px;gap:30px;">
            <div style="text-align:center;min-width:180px;flex:1;">
              <div style="border-top:1px solid #333;width:100%;margin-bottom:6px;margin-top:45px;"></div>
              <p style="margin:2px 0;font-size:9.5pt;">Prescriber's Signature &amp; Stamp</p>
              <p style="margin:2px 0;font-size:9.5pt;"><strong>${esc(this.prescriberName)}</strong></p>
            </div>
            <div style="text-align:center;min-width:180px;flex:1;">
              <div style="border-top:1px solid #333;width:100%;margin-bottom:6px;margin-top:45px;"></div>
              <p style="margin:2px 0;font-size:9.5pt;">Date of Issue</p>
              <p style="margin:2px 0;font-size:9.5pt;"><strong>${esc(this.visitDate)}</strong></p>
            </div>
          </div>
          <div style="margin-top:28px;border-top:1px solid #ccc;padding-top:8px;text-align:center;font-size:8.5pt;color:#666;">
            <p style="margin-top:6px;font-style:italic;color:#999;">Powered by JiJi Health &mdash; ${esc(this.facilityName)}</p>
          </div>
        </div>`;

      const printContainer = document.createElement("div");
      printContainer.id = "ext-rx-print-container";
      printContainer.innerHTML = printHtml;
      printContainer.style.cssText = "position:fixed;top:-9999px;left:-9999px;visibility:hidden;";
      document.body.appendChild(printContainer);

      const printStyle = document.createElement("style");
      printStyle.id = "ext-rx-print-style";
      printStyle.textContent = `
        @media print {
          body > *:not(#ext-rx-print-container) { display: none !important; }
          #ext-rx-print-container {
            position: fixed !important;
            top: 0 !important; left: 0 !important;
            width: 100% !important;
            visibility: visible !important;
            background: white !important;
          }
        }
      `;
      document.head.appendChild(printStyle);

      const doCleanup = () => {
        if (document.getElementById("ext-rx-print-container")) {
          document.body.removeChild(printContainer);
        }
        if (document.getElementById("ext-rx-print-style")) {
          document.head.removeChild(printStyle);
        }
      };

      if (this.facilityLogoUrl) {
        const imgEl = printContainer.querySelector("img");
        if (imgEl && !imgEl.complete) {
          imgEl.onload  = () => { printContainer.style.visibility = ""; window.print(); doCleanup(); };
          imgEl.onerror = () => { printContainer.style.visibility = ""; window.print(); doCleanup(); };
        } else {
          printContainer.style.visibility = "";
          window.print();
          doCleanup();
        }
      } else {
        printContainer.style.visibility = "";
        window.print();
        doCleanup();
      }
    },

    async confirmAvailability() {
      if (!this.prescriptionForm.inventory_item_id) {
        this.$toast.error("Please select a drug."); return;
      }
      if (!this.prescriptionForm.quantity || this.prescriptionForm.quantity <= 0) {
        this.$toast.error("Please enter a dispensing quantity before confirming availability."); return;
      }
      this.isConfirmingAvailability = true;
      try {
        const payload = {
          inventory_item_id: this.prescriptionForm.inventory_item_id.id,
          quantity: this.prescriptionForm.quantity,
        };
        const response = await apiClient.checkInventoryItemAvailability(payload);
        if (response.success && response.data.available) {
          this.isAvailabilityConfirmed = true;
          this.$toast.success("Drug availability confirmed.");
        } else {
          this.isAvailabilityConfirmed = false;
          this.$toast.error("Drug is not available in sufficient quantity.");
        }
      } catch {
        this.isAvailabilityConfirmed = false;
        this.$toast.error("Failed to confirm availability. Please check your internet connection.");
      } finally { this.isConfirmingAvailability = false; }
    },

    triggerPrescriptionDeleteModal(prescriptionId) {
      this.selectedExistingPrescription = prescriptionId;
      this.showPrescriptionDeleteConfirm = true;
    },
    closeConfirmationModal() {
      this.showPrescriptionDeleteConfirm = false;
      this.showPrescriptionDeleteLoader = false;
    },
    async deleteSavedConsultation() {
      try {
        this.showPrescriptionDeleteLoader = true;
        const response = await apiClient.deletePrescription(this.selectedExistingPrescription);
        this.showPrescriptionDeleteLoader = false;
        if (response.success) {
          this.$toast.success(response.data.message);
          const p = this.prescriptions.find((p) => p.id === this.selectedExistingPrescription);
          if (p) p.status.code = "ST-019";
          this.showPrescriptionDeleteConfirm = false;
        } else { this.$toast.error(response.data.message); }
      } catch {
        this.showPrescriptionDeleteLoader = false;
        this.$toast.error("Unable to delete prescription item, please check your internet connection.");
      }
    },

    async onSearch(search, loading) {
      if (search.length < 3) return;
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.isLoading = true; loading(true);
      this.searchTimeout = setTimeout(async () => {
        try { await this.searchDrug(search); } catch { this.drugs = []; }
        finally { this.isLoading = false; loading(false); }
      }, 300);
    },

    async searchDrug(searchString) {
      if (searchString.length < 3) return;
      this.isLoading = true;
      const response = await apiClient.searchConsultationInventoryItems({ name: searchString });
      this.isLoading = false;
      if (response.success) this.drugs = response.data;
      else this.$toast.error("Unable to search drugs.");
    },

    async fetchAdminstrationRoutes() {
      try {
        const response = await apiClient.getAdminstrationRoutes();
        if (response.success) this.adminstrationRoutes = response.data;
        else this.$toast.error("Unable to fetch administration routes.");
      } catch { this.$toast.error("Unable to fetch administration routes, please check your internet connection."); }
    },

    async fetchDosageFrequencies() {
      try {
        const response = await apiClient.getDosageFrequencies();
        if (response.success) this.dosageFrequencies = response.data;
        else this.$toast.error("Unable to fetch dosage frequencies.");
      } catch { this.$toast.error("Unable to fetch dosage frequencies, please check your internet connection."); }
    },

    onDrugChange(event) {
      const drug = event;
      if (drug && drug.total_quantity <= 0) {
        this.$toast.error("Cannot select this item: Out of stock (quantity is 0).");
        this.prescriptionForm.inventory_item_id = null; return;
      }
      this.selectDosageForm = drug?.drug_dosage_form?.name || "";
      this.selectedStrength = drug?.strength || "";
      this.selectedStrengthUnit = drug?.drug_strength_unit?.name || "";
      this.selectedDrugDosageForm = drug?.drug_dosage_form?.name || "";
      this.selectedRate = 0;
      this.isAvailabilityConfirmed = false;

      this.checkDrugAllergy(drug);
    },

    async checkDrugAllergy(drug) {
      if (!drug?.id) return;

      try {
        const response = await apiClient.checkDrugAllergy({
          patient_id: this.visitDetails?.patient?.PatientId,
          inventory_item_id: drug.id
        })

        if (response.success && response.data.allergic) {
          this.allergyMessage = response.data.message
          this.showAllergyConfirm = true
        }

      } catch {
        this.$toast.error("Unable to check drug allergy. Please verify manually before prescribing.");
      }
    },

    confirmAllergyOverride() {
      this.allergyAcknowledged = true;
      this.showAllergyConfirm = false;
    },

    cancelAllergySelection() {
      this.prescriptionForm.inventory_item_id = null;
      this.allergyAcknowledged = false;
      this.showAllergyConfirm = false;
    },

    addPrescriptions() {
      if (!this.isAvailabilityConfirmed) {
        this.$toast.error("Please confirm drug availability before adding the prescription."); return;
      }
      if (!this.prescriptionForm.quantity || this.prescriptionForm.quantity <= 0) {
        this.$toast.error("Please enter a dispensing quantity."); return;
      }
      if (
        this.prescriptionForm.inventory_item_id &&
        this.prescriptionForm.dose &&
        this.prescriptionForm.inventory_dosage_frequency_id &&
        this.prescriptionForm.days &&
        this.prescriptionForm.administration_route_id
      ) {
        this.prescriptions.push({ ...this.prescriptionForm, acknowledge_allergy_override: this.allergyAcknowledged });
        this.resetForm();
        this.isAvailabilityConfirmed = false;
      } else {
        this.$toast.error("Please fill all required fields in the prescription form.");
      }
    },

    submitPrescriptions() { return this.prescriptions; },

    resetForm() {
      this.prescriptionForm = {
        inventory_item_id: null, dose: "", inventory_dosage_frequency_id: null,
        days: "", administration_route_id: null, unit_price: 200,
        bill_category: 4, quantity: "", notes: "", status: {},
      };
      this.selectDosageForm = ""; this.selectedStrength = ""; this.selectedStrengthUnit = "";
      this.selectedDrugDosageForm = ""; this.selectedRate = ""; this.isAvailabilityConfirmed = false;
      this.allergyAcknowledged = false;
    },

    deletePrescription(index) { this.prescriptions.splice(index, 1); },
  },
};
</script>

<style>
.flowbite-select .vs__dropdown-toggle {
  @apply border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500;
}
.flowbite-select .vs__dropdown-menu {
  @apply border-gray-300 rounded-lg shadow-sm;
}

.print-doc {
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 11pt;
  color: #111;
  padding: 16mm 20mm;
  max-width: 210mm;
  margin: 0 auto;
  background: white;
}
.print-header {
  border-bottom: 3px double #1a365d;
  padding-bottom: 12px;
  margin-bottom: 14px;
}
.print-identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 12px;
}
.print-logo-center {
  max-height: 80px;
  max-width: 160px;
  object-fit: contain;
  margin-bottom: 8px;
}
.print-logo-center-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.print-facility-name-center {
  font-size: 20pt;
  font-weight: bold;
  color: #1a365d;
  margin: 0 0 4px 0;
  line-height: 1.2;
}
.print-facility-location {
  font-size: 10.5pt;
  color: #444;
  margin: 0 0 2px 0;
}
.print-facility-phone {
  font-size: 9.5pt;
  color: #666;
  margin: 0;
}
.print-title-band {
  background: #1a365d;
  color: #fff;
  text-align: center;
  padding: 5px 0;
  border-radius: 4px;
  margin: 0 0 10px 0;
}
.print-title-band h2 {
  margin: 0;
  font-size: 13pt;
  letter-spacing: 4px;
  font-weight: 700;
}
.print-meta { display: flex; gap: 30px; margin-top: 10px; }
.print-meta-col { flex: 1; }
.print-meta-col p { margin: 4px 0; font-size: 10.5pt; }
.print-instructions {
  margin: 12px 0; padding: 8px 14px; background: #fffbeb;
  border-left: 4px solid #d4a017; font-size: 10.5pt; border-radius: 2px; color: #333;
}
.print-table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 10pt; }
.print-table th { background: #1a365d; color: #fff; padding: 7px 8px; text-align: left; font-weight: 600; font-size: 9.5pt; }
.print-table td { padding: 6px 8px; border-bottom: 1px solid #e2e2e2; vertical-align: top; }
.print-table tr:nth-child(even) td { background: #f7f9fc; }
.print-signature { display: flex; justify-content: space-between; margin-top: 50px; gap: 30px; }
.sig-block { text-align: center; min-width: 180px; flex: 1; }
.sig-line { border-top: 1px solid #333; width: 100%; margin-bottom: 6px; margin-top: 45px; }
.sig-block p { margin: 2px 0; font-size: 9.5pt; }
.print-footer { margin-top: 28px; border-top: 1px solid #ccc; padding-top: 8px; text-align: center; font-size: 8.5pt; color: #666; }
.print-footer p { margin: 3px 0; }
.print-powered { margin-top: 6px; font-style: italic; color: #999; }
</style>